import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Link2Off,
  Loader2,
  Play,
  Plug,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import {
  integrationsApi,
  isCrmIntegration,
  type Integration,
} from "@/features/integrations/api";
import {
  INTEGRATION_CATALOG,
  type CatalogEntry,
} from "@/features/integrations/catalog";
import { env } from "@/lib/env";
import { cn } from "@/lib/utils";

const KEY = ["integrations"] as const;

export default function IntegrationsPage() {
  const role = useAuthStore((s) => s.user?.role ?? "member");
  const canEdit = hasMinRole(role, "manager");
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: [...KEY, "list"],
    queryFn: integrationsApi.list,
    staleTime: 30_000,
  });

  const byType = useMemo(() => {
    const map: Record<string, Integration> = {};
    (q.data ?? []).forEach((i) => {
      map[i.integration_type] = i;
    });
    return map;
  }, [q.data]);

  const [dialogEntry, setDialogEntry] = useState<CatalogEntry | null>(null);

  const disconnect = useMutation({
    mutationFn: (id: string) => integrationsApi.disconnect(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });

  async function onDisconnect(typeId: string) {
    const existing = byType[typeId];
    if (!existing) return;
    try {
      await disconnect.mutateAsync(existing.id);
      toast.success("Disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Integrations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect {env.platformName} with your favorite tools. Credentials are
          encrypted at rest.
        </p>
      </div>

      {q.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      )}

      {!q.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INTEGRATION_CATALOG.map((c) => (
            <IntegrationCard
              key={c.id}
              catalog={c}
              integration={byType[c.id]}
              canEdit={canEdit}
              onConnect={() => setDialogEntry(c)}
              onDisconnect={() => onDisconnect(c.id)}
            />
          ))}
        </div>
      )}

      <CredentialDialog
        entry={dialogEntry}
        existing={dialogEntry ? byType[dialogEntry.id] : undefined}
        onClose={() => setDialogEntry(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: [...KEY, "list"] })}
      />
    </div>
  );
}

function IntegrationCard({
  catalog,
  integration,
  canEdit,
  onConnect,
  onDisconnect,
}: {
  catalog: CatalogEntry;
  integration?: Integration;
  canEdit: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const qc = useQueryClient();
  const isConnected = !!integration;
  const hasCreds = !!integration?.has_credentials;
  const isCrm = isCrmIntegration(catalog.id);
  const autoPushEnabled =
    !!integration?.config &&
    (integration.config as { auto_push_summaries?: boolean })
      .auto_push_summaries === true;

  const [testResult, setTestResult] = useState<{
    status: string;
    message: string;
  } | null>(null);
  const testMut = useMutation({
    mutationFn: () => integrationsApi.test(integration!.id),
    onSuccess: (data) => setTestResult(data),
    onError: (err) => {
      const e = err as { response?: { data?: { message?: string } } };
      setTestResult({
        status: "error",
        message: e?.response?.data?.message || "Test failed",
      });
    },
  });

  const autoPushMut = useMutation({
    mutationFn: (next: boolean) =>
      integrationsApi.update(integration!.id, {
        config: {
          ...(integration?.config || {}),
          auto_push_summaries: next,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations", "list"] }),
    onError: () => toast.error("Failed to update auto-push setting"),
  });

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-start gap-3">
          <span className="text-3xl leading-none">{catalog.icon}</span>
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-base">{catalog.name}</CardTitle>
              {isConnected && hasCreds && (
                <CheckCircle2 className="size-4 text-emerald-500" />
              )}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {catalog.description}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {isConnected && (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
              hasCreds
                ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
                : "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
            )}
          >
            {hasCreds ? "Connected" : "No credentials"}
          </span>
        )}

        {canEdit && isCrm && isConnected && hasCreds && (
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border bg-card px-3 py-2 text-xs">
            <div className="min-w-0">
              <div className="font-medium">
                Send every call to {catalog.name}
              </div>
              <div className="text-muted-foreground">
                {autoPushEnabled
                  ? `On — each call's summary goes to ${catalog.name} when the call ends.`
                  : `Off — calls are only sent when you press "Push to CRM" on a call.`}
              </div>
            </div>
            <Switch
              checked={autoPushEnabled}
              disabled={autoPushMut.isPending}
              onCheckedChange={(v) => autoPushMut.mutate(v)}
            />
          </label>
        )}

        {testResult && (
          <div
            className={cn(
              "flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-xs",
              testResult.status === "success" ||
                testResult.status === "connected"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400"
                : "border-destructive/30 bg-destructive/10 text-destructive",
            )}
          >
            <span>{testResult.message}</span>
            <button
              type="button"
              onClick={() => setTestResult(null)}
              className="text-[10px] underline-offset-2 hover:underline"
            >
              dismiss
            </button>
          </div>
        )}

        <div className="flex gap-2">
          {canEdit && (
            <Button className="flex-1" onClick={onConnect} size="sm">
              <Plug className="size-4" />
              {isConnected ? "Update credentials" : "Connect"}
            </Button>
          )}
          {canEdit && isConnected && hasCreds && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => testMut.mutate()}
              disabled={testMut.isPending}
              aria-label="Test connection"
            >
              {testMut.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Play className="size-4" />
              )}
            </Button>
          )}
          {canEdit && isConnected && (
            <Button
              variant="outline"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={onDisconnect}
              aria-label="Disconnect"
            >
              <Link2Off className="size-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CredentialDialog({
  entry,
  existing,
  onClose,
  onSaved,
}: {
  entry: CatalogEntry | null;
  existing?: Integration;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (entry) {
      // Prefill non-secret saved values (username, event type id, …) so they're
      // visible and editable. Secrets are never sent by the API, so they stay blank.
      setValues(existing?.safe_credentials ?? {});
      setError("");
    }
  }, [entry, existing]);

  const save = useMutation({
    mutationFn: async () => {
      if (!entry) throw new Error("No entry");
      // Only send non-empty values so we don't overwrite saved creds with blanks
      const cleaned: Record<string, string> = {};
      Object.entries(values).forEach(([k, v]) => {
        if (v && v.trim()) cleaned[k] = v.trim();
      });
      if (existing) {
        await integrationsApi.update(existing.id, { credentials: cleaned });
      } else {
        await integrationsApi.create({
          name: entry.name,
          integration_type: entry.id,
          credentials: cleaned,
        });
      }
    },
    onSuccess: () => {
      toast.success("Credentials saved");
      onSaved();
      onClose();
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e?.response?.data?.error || "Failed to save credentials.");
    },
  });

  if (!entry) return null;
  const savedKeys = existing?.credential_keys ?? [];

  return (
    <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <span className="mr-2 text-xl leading-none">{entry.icon}</span>
            {entry.name} — Credentials
          </DialogTitle>
          <DialogDescription>
            Credentials are encrypted at rest. Secrets like API keys stay hidden —
            leave them blank to keep the saved value.
          </DialogDescription>
        </DialogHeader>

        {entry.setupSteps && (
          <ol className="list-decimal space-y-1 rounded-md border bg-muted/40 py-2.5 pl-8 pr-3 text-xs text-muted-foreground">
            {entry.setupSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        )}

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-3"
        >
          {/* Decoy fields to absorb autofill */}
          <input
            type="text"
            name="decoy_user"
            autoComplete="username"
            className="hidden"
            tabIndex={-1}
          />
          <input
            type="password"
            name="decoy_pass"
            autoComplete="current-password"
            className="hidden"
            tabIndex={-1}
          />

          {entry.fields.map((f) => {
            // Only secrets (API keys etc.) are masked/blank-to-keep. Non-secret
            // fields are prefilled with their saved value so they're visible.
            const isSecret = f.type === "password";
            const isSavedSecret = isSecret && savedKeys.includes(f.key);
            return (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`field-${f.key}`}>
                  {f.label}
                  {isSavedSecret && (
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      (saved)
                    </span>
                  )}
                </Label>
                <Input
                  id={`field-${f.key}`}
                  type={f.type || "text"}
                  value={values[f.key] || ""}
                  onChange={(e) =>
                    setValues({ ...values, [f.key]: e.target.value })
                  }
                  placeholder={
                    isSavedSecret
                      ? "•••••••• (leave blank to keep)"
                      : f.placeholder
                  }
                  autoComplete="off"
                  name={`field_${entry.id}_${f.key}_${Date.now()}`}
                  data-form-type="other"
                />
                {isSavedSecret && (
                  <p className="text-xs text-muted-foreground">
                    Leave blank to keep existing value.
                  </p>
                )}
              </div>
            );
          })}

          {existing?.auto_settings?.timezone && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
              Auto-detected timezone:{" "}
              <strong>{existing.auto_settings.timezone}</strong>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save Credentials"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
